import { useId, useState, type HTMLAttributes, type ReactNode } from 'react'
import './surface.css'

// title wird bewusst neu deklariert: das native HTML-Attribut erlaubt nur einen
// String, hier soll aber Markup in die Kopfzeile passen.
export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Kopfzeile mit Titel und optionaler Aktion. */
  title?: ReactNode
  eyebrow?: ReactNode
  actions?: ReactNode
  /** Ohne Innenabstand — fuer Tabellen, die bis an den Rand laufen sollen. */
  flush?: boolean
}

export function Card({
  title,
  eyebrow,
  actions,
  flush,
  children,
  className,
  ...rest
}: CardProps) {
  const hasHeader = Boolean(title || eyebrow || actions)

  return (
    <section className={['card', className].filter(Boolean).join(' ')} {...rest}>
      {hasHeader && (
        <header className="card__header">
          <div className="card__heading">
            {eyebrow && <p className="eyebrow">{eyebrow}</p>}
            {title && <h2 className="card__title">{title}</h2>}
          </div>
          {actions && <div className="card__actions">{actions}</div>}
        </header>
      )}
      <div className="card__body" data-flush={flush || undefined}>
        {children}
      </div>
    </section>
  )
}

/** Schlichte Flaeche ohne Kopfzeile. */
export function Panel({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={['panel', className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </div>
  )
}

export interface TabItem {
  id: string
  label: ReactNode
  content: ReactNode
  /** Kleiner Zaehler rechts vom Label. */
  count?: number
}

export interface TabsProps {
  items: TabItem[]
  defaultId?: string
  'aria-label': string
}

/**
 * Registerkarten mit Tastaturbedienung nach WAI-ARIA: Pfeiltasten wechseln,
 * Pos1/Ende springen an den Rand. Nur die aktive Karte liegt im Tab-Fluss.
 */
export function Tabs({ items, defaultId, 'aria-label': ariaLabel }: TabsProps) {
  const baseId = useId()
  const [active, setActive] = useState(defaultId ?? items[0]?.id)

  if (items.length === 0) return null

  const activeIndex = Math.max(
    0,
    items.findIndex((item) => item.id === active),
  )

  function move(delta: number) {
    const next = (activeIndex + delta + items.length) % items.length
    setActive(items[next].id)
    document.getElementById(`${baseId}-tab-${items[next].id}`)?.focus()
  }

  return (
    <div className="tabs">
      <div className="tabs__list" role="tablist" aria-label={ariaLabel}>
        {items.map((item) => {
          const selected = item.id === items[activeIndex].id
          return (
            <button
              key={item.id}
              id={`${baseId}-tab-${item.id}`}
              type="button"
              role="tab"
              className="tabs__tab"
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${item.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setActive(item.id)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowRight') {
                  event.preventDefault()
                  move(1)
                } else if (event.key === 'ArrowLeft') {
                  event.preventDefault()
                  move(-1)
                } else if (event.key === 'Home') {
                  event.preventDefault()
                  move(-activeIndex)
                } else if (event.key === 'End') {
                  event.preventDefault()
                  move(items.length - 1 - activeIndex)
                }
              }}
            >
              {item.label}
              {item.count != null && (
                <span className="tabs__count numeric">{item.count}</span>
              )}
            </button>
          )
        })}
      </div>

      {items.map((item) => (
        <div
          key={item.id}
          id={`${baseId}-panel-${item.id}`}
          role="tabpanel"
          aria-labelledby={`${baseId}-tab-${item.id}`}
          className="tabs__panel"
          hidden={item.id !== items[activeIndex].id}
          tabIndex={0}
        >
          {item.id === items[activeIndex].id && item.content}
        </div>
      ))}
    </div>
  )
}
